import { useCallback } from 'react';
import { type DefaultError, useMutation } from '@tanstack/react-query';

import { logger } from '@zep/core-logger';

import { AI_HTTP_CLIENT } from '@/api/ai/client';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowAiErrorAlert } from '@/views/build/hook';

import { useQuizFormField } from '../../hook/useQuizFormField';
import { useQuizControl } from '../../QuizControlProvider';

import { type AiGenerateSingleSubjectiveAnswerQuery } from './type';
import { useSetAiGenerateSubjectiveStatus } from './useAiGeneateSubjectiveStatus';

export interface AiGenerateSingleSubjectiveSimilarAnswerQuery
  extends AiGenerateSingleSubjectiveAnswerQuery {
  referenceAnswer: string;
}

export const useAiGenerateSingleSimilarAnswer = (answerIndex: number) => {
  const { mutateAsync, isPending } =
    useAiGenerateSingleSubjectiveSimilarAnswerMutation();

  const [similarAnswer] = useQuizFormField({
    field: `subjectiveData.answerList.${answerIndex}.text`,
  });

  const [referenceAnswer] = useQuizFormField({
    field: 'subjectiveData.answerList.0.text',
  });

  const [question] = useQuizFormField({
    field: 'question',
  });

  const { id } = useQuizControl();
  const { setValue, getValues } = useBuildFormContext();
  const disabled = isPending || !question;

  const { addIndex, removeIndex } = useSetAiGenerateSubjectiveStatus();

  const showAiErrorAlert = useShowAiErrorAlert();

  const handleGeneratedAnswer = useCallback(
    (generatedAnswer: string) => {
      const quizList = getValues(`quizList`);
      const targetQuizIndex = quizList.findIndex(quiz => quiz.id === id);
      if (targetQuizIndex === -1) {
        console.error(`target quiz not found: ${id}`);
        return;
      }

      setValue(
        `quizList.${targetQuizIndex}.subjectiveData.answerList.${answerIndex}.text`,
        generatedAnswer,
      );
    },
    [setValue, id, getValues, answerIndex],
  );

  const generate = useCallback(
    async (answerIndex: number) => {
      addIndex(answerIndex);
      const start = Date.now();
      logger.track('add_subjective_answer_ai_start', {
        location: 'single',
      });
      try {
        const answer = await mutateAsync({
          question,
          previousOutput: similarAnswer,
          referenceAnswer, // 첫 번째 정답을 참조
        });
        answer && handleGeneratedAnswer(answer);
        const duration = (Date.now() - start) / 1000;
        logger.track('add_subjective_answer_ai_end', {
          location: 'single',
          duration_seconds: duration,
        });
      } catch (_) {
        const duration = (Date.now() - start) / 1000;
        logger.track('add_subjective_answer_ai_error', {
          location: 'single',
          duration_seconds: duration,
        });
        showAiErrorAlert();
      } finally {
        removeIndex(answerIndex);
      }
    },
    [
      mutateAsync,
      question,
      similarAnswer,
      referenceAnswer,
      handleGeneratedAnswer,
      showAiErrorAlert,
      addIndex,
      removeIndex,
    ],
  );

  return {
    disabled,
    generate,
  };
};

export const useAiGenerateSingleSubjectiveSimilarAnswerMutation = () => {
  return useMutation<
    string,
    DefaultError,
    AiGenerateSingleSubjectiveSimilarAnswerQuery
  >({
    mutationFn: async query => {
      const res = await AI_HTTP_CLIENT.post(
        `/quiz-builder/generate/single-subjective-similar-answer`,
        query,
      );
      return res.data;
    },
  });
};
